/**
 * Admin dashboard real-time "new reply" notifications.
 *
 * GitHub issue #499 ("E2E: Admin dashboard — outreach metrics, company
 * filter, job table") "Real-time Updates" test-case group: "WebSocket
 * notification for new reply (mock) shows toast" / QA "[edge]" case
 * ("Given a superuser on /admin viewing the sourcing jobs table, when a
 * mocked WebSocket 'new reply' notification is received, then a toast
 * notifying of the new reply is displayed without a full page reload").
 *
 * Backend note: `apps/workers/src/index.ts` already defines a
 * `RealtimeFanout` Durable Object ("Replaces redis_listener pub/sub for
 * WebSocket fan-out") but it is not mounted on any Hono route yet (no
 * `app.get('/realtime'|'/admin/realtime', ...)` upgrade handler exists),
 * and nothing publishes a `new_reply` event into it when
 * `sync-replies`/webhook inbound-email handling runs. `ADMIN_WS_PATH`
 * below is this feature's assumed wire contract for when that lands —
 * TODO(backend): mount `RealtimeFanout` at this path and broadcast
 * `{ type: 'new_reply', jobId, jobTitle, candidateName }` from wherever
 * inbound replies are ingested.
 *
 * This hook is deliberately best-effort / non-critical: a superuser who
 * never receives a `new_reply` message (dev without the backend route,
 * or a WebSocket connection failure) still sees the rest of
 * `AdminDashboard` render normally — it degrades silently rather than
 * throwing, per this repo's "browser code must render even with no
 * configuration" convention (see CLAUDE.md rule 8).
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useConfigOptional } from '@/app/providers';

export const ADMIN_WS_PATH = '/admin/realtime';

export interface NewReplyNotification {
  type: 'new_reply';
  jobId?: string;
  jobTitle?: string;
  candidateName?: string;
}

/** Narrow an arbitrary parsed WebSocket message down to a
 * {@link NewReplyNotification}, ignoring anything else (future message
 * types, malformed payloads, etc). */
export function isNewReplyNotification(value: unknown): value is NewReplyNotification {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'new_reply'
  );
}

/** Converts a runtime-config `apiBaseUrl` (e.g. `http://localhost:8787/api`)
 * into a same-origin WebSocket URL for {@link ADMIN_WS_PATH} (e.g.
 * `ws://localhost:8787/admin/realtime`). Returns `null` if `apiBaseUrl`
 * can't be parsed as a URL (config not loaded yet, or malformed). */
export function buildAdminRealtimeUrl(apiBaseUrl: string): string | null {
  try {
    const parsed = new URL(apiBaseUrl);
    parsed.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    parsed.pathname = ADMIN_WS_PATH;
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Subscribes to {@link ADMIN_WS_PATH} for the lifetime of the calling
 * component and fires a `sonner` toast (rendered by the app-wide
 * `<Toaster />` in `AppShell.tsx`) whenever a `new_reply` message
 * arrives — no query invalidation/refetch, no page reload, matching the
 * ticket's "without a full page reload" requirement.
 */
export function useAdminReplyNotifications(): void {
  const config = useConfigOptional();
  const apiBaseUrl = config?.apiBaseUrl;

  useEffect(() => {
    if (!apiBaseUrl || typeof window === 'undefined' || typeof WebSocket === 'undefined') {
      return;
    }

    const wsUrl = buildAdminRealtimeUrl(apiBaseUrl);
    if (!wsUrl) return;

    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl);
    } catch {
      // WebSocket construction can throw synchronously for a malformed
      // URL — degrade silently (see module doc-comment).
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(typeof event.data === 'string' ? event.data : '');
      } catch {
        return;
      }
      if (!isNewReplyNotification(parsed)) return;

      const who = parsed.candidateName ? `${parsed.candidateName}` : 'A candidate';
      const where = parsed.jobTitle ? ` on "${parsed.jobTitle}"` : '';
      toast(`New reply from ${who}${where}`, {
        description: 'A candidate replied to an outreach email.',
      });
    };

    socket.addEventListener('message', handleMessage);
    // Intentionally no `onerror`/`onclose` reconnect loop here — this is
    // a best-effort notification channel, not a durable subscription;
    // a dropped connection simply stops toasts until the next visit to
    // `/admin` re-mounts this hook.

    return () => {
      socket.removeEventListener('message', handleMessage);
      socket.close();
    };
  }, [apiBaseUrl]);
}
